import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, Loader2, Wallet, Ban } from 'lucide-react';
import { Button } from '../ui/Button';
import { useBalance, useOpenPosition, usePositions } from '../../hooks/usePortfolio';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../utils/cn';
import { getErrorMessage } from '../../utils/api';
import { SentimentVoteOption } from '../../types/sentiment';
import type { TradingPosition } from '../../types/portfolio';

interface OpenPositionFormProps {
  stockSymbol: string;
  category: string;
  gameDate?: string;
  userVote: SentimentVoteOption | null;
  onSuccess?: () => void;
}

interface StoredOpenPosition {
  direction: 'long' | 'short';
  investedCoins: number;
  timestamp: string;
}

/**
 * Form component for opening a new paper trading position
 * Displayed in FinishedModal after sentiment voting
 *
 * Trading direction is tied to sentiment vote:
 * - buy → long only
 * - sell → short only
 * - hold → trading disabled
 * - null → must vote first
 */
export const OpenPositionForm: React.FC<OpenPositionFormProps> = ({
  stockSymbol,
  category,
  gameDate,
  userVote,
  onSuccess,
}) => {
  const { user } = useAuth();
  const { data: balanceData, isLoading: balanceLoading } = useBalance();
  const {
    data: positionsData,
    isLoading: positionsLoading,
    refetch: refetchPositions
  } = usePositions('open');
  const positions = positionsData?.positions;
  const maxPositions = positionsData?.maxPositions ?? 3;
  const openPositionMutation = useOpenPosition();

  const navigate = useNavigate();
  const [investedCoins, setInvestedCoins] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [optimisticPosition, setOptimisticPosition] = useState<StoredOpenPosition | null>(null);

  const isAuthenticated = Boolean(user);
  const balance = balanceData?.balanceCoins ?? 0;
  const openPositionCount = positions?.length ?? 0;
  const isAtPositionLimit = openPositionCount >= maxPositions;
  const isLoading = balanceLoading || positionsLoading;

  const matchingPosition = useMemo(() => {
    if (!positions || !stockSymbol) {
      return null;
    }

    const normalizedSymbol = stockSymbol.toUpperCase();
    const normalizedCategory = (category || '').toLowerCase();
    const normalizedGameDate = gameDate ?? null;

    return (
      positions.find((position: TradingPosition) => {
        const matchesSymbol = position.stockSymbol?.toUpperCase() === normalizedSymbol;
        const matchesCategory = !normalizedCategory || position.category?.toLowerCase() === normalizedCategory;
        const positionDate = position.entryDate ? position.entryDate.slice(0, 10) : null;
        const matchesDate = !normalizedGameDate || positionDate === normalizedGameDate;
        return matchesSymbol && matchesCategory && matchesDate;
      }) || null
    );
  }, [positions, stockSymbol, category, gameDate]);

  useEffect(() => {
    if (matchingPosition) {
      setOptimisticPosition(null);
    }
  }, [matchingPosition]);

  // Derive direction from userVote
  const direction: 'long' | 'short' | null =
    userVote === 'buy' ? 'long' :
    userVote === 'sell' ? 'short' :
    null;

  const resolvedPosition: StoredOpenPosition | null = matchingPosition
    ? {
        direction: matchingPosition.direction,
        investedCoins: matchingPosition.investedCoins,
        timestamp: matchingPosition.entryDate,
      }
    : optimisticPosition;

  const hasResolvedPosition = Boolean(resolvedPosition);
  const successDirection = resolvedPosition?.direction ?? direction;
  const successInvestedCoins = resolvedPosition?.investedCoins ?? investedCoins;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isAuthenticated) {
      setError('Please sign in to open a position');
      return;
    }

    if (!direction) {
      setError('Invalid trading direction');
      return;
    }

    if (investedCoins > balance) {
      setError('Not enough coins');
      return;
    }

    if (investedCoins < 1) {
      setError('Minimum investment is 1 coin');
      return;
    }

    if (isAtPositionLimit) {
      setError(`You have ${maxPositions} open positions. Close one to open another.`);
      return;
    }

    try {
      await openPositionMutation.mutateAsync({
        stockSymbol,
        category,
        direction,
        investedCoins,
      });
      setOptimisticPosition({
        direction,
        investedCoins,
        timestamp: new Date().toISOString(),
      });
      refetchPositions();
      onSuccess?.();
    } catch (err: any) {
      const rawMessage = err?.details?.data?.error || getErrorMessage(err);
      const message = typeof rawMessage === 'string' ? rawMessage : 'Failed to open position';

      // Handle specific error messages from backend
      if (message.includes('Position limit')) {
        setError(`You have ${maxPositions} open positions. Close one to open another.`);
      } else if (message.includes('Insufficient') || message.includes('balance')) {
        setError('Not enough coins');
      } else if (message.includes('price') || message.includes('Price')) {
        setError('Price unavailable, try again later');
      } else {
        setError(message);
      }
    }
  };

  const handleCoinsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Strip commas and leading zeros
    const rawValue = e.target.value.replace(/,/g, '').replace(/^0+/, '') || '';
    if (rawValue === '') {
      setInvestedCoins(0);
      return;
    }
    // Only allow digits
    if (!/^\d+$/.test(rawValue)) {
      return;
    }
    const value = parseInt(rawValue, 10);
    if (!isNaN(value) && value >= 0) {
      setInvestedCoins(Math.min(value, balance));
    }
  };

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Show success state
  if (hasResolvedPosition && successDirection) {
    return (
      <div className="space-y-3 border-t border-border/60 pt-6 text-left">
        <div className="rounded-lg bg-green-50 dark:bg-green-900/30 p-4 text-center">
          <p className="text-sm font-medium text-green-700 dark:text-green-300">
            Position opened successfully!
          </p>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            {successDirection === 'long' ? 'Long' : 'Short'} ${stockSymbol} with {successInvestedCoins} coins
          </p>
          <button
            onClick={() => navigate('/portfolio')}
            className="px-6 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 hover:underline"
          >
            Open Paper Portfolio →
          </button>
        </div>
      </div>
    );
  }

  // Header component used across all states
  const Header = () => (
    <div className="flex items-center gap-2">
      <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Paper Trade
      </p>
      <sup className="text-[0.55rem] font-semibold text-blue-600 dark:text-blue-400">BETA</sup>
    </div>
  );

  // State 1: Not voted yet
  if (userVote === null) {
    return (
      <div className="space-y-3 border-t border-border/60 pt-6 text-left">
        <Header />
        <div className="rounded-lg bg-muted/50 p-4">
          <p className="text-sm text-muted-foreground">
            Vote on sentiment first to unlock paper trading.
          </p>
        </div>
      </div>
    );
  }

  // State 2: Voted Hold
  if (userVote === 'hold') {
    return (
      <div className="space-y-3 border-t border-border/60 pt-6 text-left">
        <Header />
        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-4 flex items-center gap-3">
          <Ban className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            You voted Hold — trading is disabled for this stock.
          </p>
        </div>
      </div>
    );
  }

  // State 3: At position limit
  if (isAtPositionLimit) {
    return (
      <div className="space-y-3 border-t border-border/60 pt-6 text-left">
        <Header />
        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-4">
          <div className="flex items-center gap-3 mb-3">
            <Wallet className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
              Position limit reached ({openPositionCount}/{maxPositions})
            </p>
          </div>
          <p className="text-sm text-amber-600 dark:text-amber-400 mb-3">
            Close an existing position to open a new one.
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate('/portfolio')}
            className="w-full"
          >
            Open Portfolio to Close Position
          </Button>
        </div>
      </div>
    );
  }

  // State 4: Voted Buy or Sell - show the form
  const isLong = direction === 'long';
  const DirectionIcon = isLong ? TrendingUp : TrendingDown;
  const directionLabel = isLong ? 'Long' : 'Short';
  const directionEmoji = isLong ? '📈' : '📉';
  const voteLabel = isLong ? 'Buy' : 'Sell';

  return (
    <div className="space-y-4 border-t border-border/60 pt-6 text-left">
      {/* Header */}
      <div>
        <Header />
        <p className="text-sm text-muted-foreground mt-1">
          Put your money where your mouth is
        </p>
      </div>

      {/* Balance Display */}
      <div className="flex items-center gap-2 text-sm">
        <Wallet className="h-4 w-4 text-muted-foreground" />
        <span className="text-muted-foreground">Balance:</span>
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <span className="font-semibold text-foreground">{balance.toLocaleString()} coins</span>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Direction Display (based on vote) */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Direction</label>
          <div
            className={cn(
              'flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-3',
              isLong
                ? 'bg-emerald-500/15 border-emerald-500 text-emerald-600 dark:text-emerald-100'
                : 'bg-rose-500/15 border-rose-500 text-rose-500 dark:text-rose-100'
            )}
          >
            <DirectionIcon className="h-4 w-4" />
            <span className="font-semibold">{directionLabel} {directionEmoji}</span>
            <span className="text-xs opacity-75">(based on {voteLabel} vote)</span>
          </div>
        </div>

        {/* Investment Amount */}
        <div className="space-y-2">
          <label htmlFor="investedCoins" className="text-sm font-medium text-foreground">
            Investment
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              id="investedCoins"
              placeholder="Enter number of coins"
              value={investedCoins > 0 ? investedCoins.toLocaleString() : ''}
              onChange={handleCoinsChange}
              disabled={openPositionMutation.isPending || balance === 0}
              className={cn(
                'flex-1 rounded-lg border border-input bg-background px-3 py-2 text-base sm:text-sm',
                'focus:outline-none focus:ring-2 focus:ring-primary/50',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            />
            <span className="text-sm text-muted-foreground">coins</span>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <p className="text-xs text-destructive">{error}</p>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={
            openPositionMutation.isPending ||
            balance === 0 ||
            investedCoins < 1 ||
            investedCoins > balance ||
            isAtPositionLimit
          }
          className={cn(
            'w-full text-white',
            isLong
              ? 'bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600'
              : 'bg-rose-600 hover:bg-rose-700 dark:bg-rose-500 dark:hover:bg-rose-600'
          )}
        >
          {openPositionMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Opening Position...
            </>
          ) : (
            `Open ${directionLabel} Position`
          )}
        </Button>

        {/* Position Limit Info */}
        <p className="text-xs text-center text-muted-foreground">
          {isAtPositionLimit ? (
            <span className="text-amber-600 dark:text-amber-400">
              Position limit reached ({openPositionCount}/{maxPositions}). Close a position to open another.
            </span>
          ) : (
            `Open positions: ${openPositionCount}/${maxPositions}`
          )}
        </p>
      </form>
    </div>
  );
};

export default OpenPositionForm;
